import {baseApi} from 'core/redux/store/api';
import {API_VERSION} from 'core/helpers/env';
import {IFilterFacetResponse} from 'plugins/modules/filter/types';
import {TGetProductsResponse} from 'modules/products/models';
import {createListingApiService, TGetListingRequest} from 'plugins/modules/listing/api/api';
import {IProductShort, ISearchMeta} from 'modules/products/types';
import {TGetListingTransformedResponse} from 'plugins/modules/listing/types';
import {buildHeaders} from 'core/helpers/api/headers';

const SEARCH_QUERY_PARAM_NAME = 'q';
const SEARCH_PATH_ROOT = `${API_VERSION}/catalog/search`;
const SEARCH_FILTERS_PATH_ROOT = `${API_VERSION}/catalog/search`;

export interface IGetSearchFiltersProps {
    q: string;
    section?: string;
    filter?: string;
    withSeo?: boolean;
    withMeta?: boolean;
}

interface ISearchQueryParams {
    q: string;
    section?: string | null;
    filter?: string;
    sort?: string;
    pageNumber: number;
    pageSize: number;
    withMeta?: boolean;
    withSeo?: boolean;
}

const buildSearchFiltersUrl = (params: IGetSearchFiltersProps): string => {
    const {filter, q, section, withMeta} = params;

    const trimmedQuery = q?.trim();
    const queryString = trimmedQuery ? `${SEARCH_QUERY_PARAM_NAME}=${encodeURIComponent(trimmedQuery)}` : '';

    let url = `${SEARCH_FILTERS_PATH_ROOT}/facetsearch?${queryString}${section !== undefined ? `&section=${section}` : ''}&withMeta=${withMeta ? 1 : 0}`;

    if (filter) {
        url += `&${filter}`;
    }

    return url;
};

const buildSearchUrl = (params: ISearchQueryParams): string => {
    const {q, section, filter, sort, pageNumber, pageSize, withMeta} = params;
    const sortParam = sort ? `&sort=${sort}` : '';
    const filterParam = filter ? `&${filter}` : '';

    const trimmedQuery = q?.trim();
    const queryString = trimmedQuery ? `${SEARCH_QUERY_PARAM_NAME}=${encodeURIComponent(trimmedQuery)}` : '';

    return `${SEARCH_PATH_ROOT}?${queryString}${filterParam}&pageNumber=${pageNumber}&pageSize=${pageSize}${sortParam}${section !== undefined && null !== section ? `&section=${section}` : ''}&withMeta=${
        withMeta ? 1 : 0
    }`;
};

export const searchApiService = baseApi.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        getSearchFilters: builder.query({
            providesTags: ['FacetFilters'],
            query: (params: IGetSearchFiltersProps) => {
                const headers = buildHeaders(params.withSeo ? {withSeo: 'list'} : {});
                const url = buildSearchFiltersUrl(params);

                return {url, headers};
            },
            transformResponse: (response: {data: IFilterFacetResponse}) => response?.data,
            serializeQueryArgs: ({endpointName, queryArgs}) =>
                `${endpointName}/${queryArgs.q}/${queryArgs.section}/${queryArgs.filter}`,
        }),
    }),
});

export type TSearchListingTransformedResponse = TGetListingTransformedResponse<
    IProductShort,
    {
        searchMeta?: ISearchMeta | null;
    }
>;

const productsListingApiService = createListingApiService<
    IProductShort,
    TGetProductsResponse,
    TSearchListingTransformedResponse,
    TGetListingRequest<{q: string; section?: string | null}>
>({
    name: 'getSearchProducts',
    query: (query) => {
        const headers = buildHeaders(query.withSeo ? {withSeo: 'list'} : {});

        const url = buildSearchUrl(query);

        return {url, headers};
    },
    transformResponse: (response): TSearchListingTransformedResponse => ({
        list: response?.data?.items,
        pagination: response?.pagination,
        meta: response?.meta ?? null,
        seo: response?.seo ?? null,
        searchMeta: response?.searchMeta ?? null,
    }),
    serializeQueryArgs: ({endpointName, queryArgs}) => {
        const storeId = queryArgs.expressOrderStoreId ?? '';
        return `${endpointName}/${queryArgs.q}/${queryArgs.section}/${queryArgs.filter}/${queryArgs.sort}${storeId ? `/${storeId}` : ''}`;
    },
    forceRefetchValuesKeys: ['q', 'section', 'expressOrderStoreId'],
});

export const {getSearchProducts} = productsListingApiService.endpoints;
export const searchProductsListingApiService = productsListingApiService;
export const {useLazyGetSearchProductsQuery} = productsListingApiService;
export const {
    useGetSearchFiltersQuery,
    util: {getRunningQueriesThunk},
} = searchApiService;
